/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { Grid } from '@mui/material'

import MDAudioPlayer from 'components/MDAudioPlayer'
import MDBadge from 'components/MDBadge'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { EnrichedCallInsight } from 'types/server/call-insights'

function getIntentBadgeColor(intent?: string) {
	if (intent === 'new_ride') return 'success'
	if (intent === 'update_ride') return 'info'
	if (intent === 'cancel_ride') return 'warning'
	if (intent === 'other') return 'dark'
	return 'secondary'
}

interface Props {
	data: EnrichedCallInsight
	id: string
}
function CallInsightsContent({ data, id }: Props): JSX.Element {
	return (
		<MDBox
			display={'flex'}
			justifyContent={'center'}
			alignItems={'center'}
			flexDirection={'column'}
			width={'100%'}
		>
			<MDBox width={'50%'}>
				<MDTypography variant="h4">Call Information</MDTypography>
				<MDBox
					display={'flex'}
					flexDirection={'row'}
					gap={2}
					alignItems={'center'}
					justifyContent={'space-between'}
				>
					<MDTypography variant="h6">ID:</MDTypography>
					<MDTypography variant="caption">{id}</MDTypography>
				</MDBox>
				<MDBox display={'flex'} flexDirection={'row'} mt={2} justifyContent={'space-between'}>
					<MDBox display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
						<MDTypography variant="h6">From:</MDTypography>
						<MDTypography variant="body2">{data.from_number}</MDTypography>
					</MDBox>
					<MDBox display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
						<MDTypography variant="h6">To:</MDTypography>
						<MDTypography variant="body2">{data.to_number}</MDTypography>
					</MDBox>
				</MDBox>
				<MDBox display={'flex'} flexDirection={'row'} mt={2} justifyContent={'space-between'}>
					<MDTypography variant="h6">Intent:</MDTypography>
					<MDBadge
						variant="contained"
						color={getIntentBadgeColor(data.intent)}
						badgeContent={data.intent ?? 'unknown'}
						container
					/>
				</MDBox>
			</MDBox>
			<Grid container mt={2} spacing={1} justifyContent={'space-evenly'}>
				<Grid item xs={12} lg={4} justifyContent={'center'}>
					<MDBox display={'flex'} flexDirection={'column'} gap={2}>
						<MDBox>
							<MDTypography mb={1} variant="h6">
								Recording:
							</MDTypography>
							<MDAudioPlayer controlsList="novolume" src={data.recording_url} />
						</MDBox>

						<MDBox
							display={'flex'}
							flexDirection={'row'}
							gap={2}
							alignItems={'center'}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6">Date:</MDTypography>
							<MDTypography variant="body2">
								{new Date(data.start_timestamp).toLocaleString(undefined, {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
									hour: '2-digit',
									minute: '2-digit',
								})}
							</MDTypography>
						</MDBox>

						<MDBox
							display={'flex'}
							flexDirection={'row'}
							gap={2}
							alignItems={'center'}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6">Duration(s):</MDTypography>
							<MDTypography variant="body2">{data.duration}</MDTypography>
						</MDBox>
						<MDBox
							display={'flex'}
							flexDirection={'row'}
							gap={2}
							alignItems={'center'}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6">Success Status:</MDTypography>
							<MDTypography variant="body2">{data.success_status}</MDTypography>
						</MDBox>
						<MDBox
							display={'flex'}
							flexDirection={'row'}
							gap={2}
							alignItems={'center'}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6">Call Status:</MDTypography>
							<MDBadge variant="contained" color="dark" badgeContent={data.status} container />
						</MDBox>

						<MDBox
							display={'flex'}
							flexDirection={'row'}
							gap={2}
							alignItems={'center'}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6">Sentiment:</MDTypography>
							<MDBadge
								variant="contained"
								color={
									data.sentiment === 'Positive'
										? 'success'
										: data.sentiment === 'Negative'
										? 'error'
										: 'dark'
								}
								badgeContent={data.sentiment}
								container
							/>
						</MDBox>
						<MDBox
							display={'flex'}
							flexDirection={'column'}
							textAlign={'left'}
							gap={2}
							justifyContent={'space-between'}
						>
							<MDTypography variant="h6" textAlign={'left'}>
								Summary:
							</MDTypography>
							<MDTypography variant="body2">
								<i>{data.summary}</i>
							</MDTypography>
						</MDBox>
					</MDBox>
				</Grid>
				<Grid item xs={12} lg={6}>
					<MDTypography variant="h6" mb={1}>
						Transcript
					</MDTypography>
					<MDBox
						mb={2}
						overflow={'auto'}
						height={'500px'}
						pr={1}
						sx={{
							'&::-webkit-scrollbar': {
								width: '6px',
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: 'rgba(0, 0, 0, 0.05)', // Color of the thumb
								borderRadius: '4px',
							},
							'&::-webkit-scrollbar-thumb:hover': {
								backgroundColor: 'rgba(0, 0, 0, 0.3)', // Color on hover
							},
							'&::-webkit-scrollbar-track': {
								background: 'transparent',
							},
							'&:hover': {
								'&::-webkit-scrollbar': {
									display: 'block',
								},
							},
						}}
					>
						{data.parsed_transcript?.map((message, index) => (
							<MDBox
								key={index}
								display="flex"
								justifyContent={message.role === 'agent' ? 'flex-end' : 'flex-start'}
								mb={2}
							>
								<MDBox
									color="white"
									px={2}
									py={1}
									borderRadius={'md'}
									bgColor={message.role === 'agent' ? 'info' : 'secondary'}
									maxWidth="75%"
								>
									<MDTypography variant="body2" color="white">
										{message.content}
									</MDTypography>
								</MDBox>
							</MDBox>
						))}
						<MDBox display={'flex'} justifyContent={'center'} width={'100%'}>
							<MDTypography variant="caption">{`Conversation End (${
								data.disconnection_reason || ''
							})`}</MDTypography>
						</MDBox>
					</MDBox>
				</Grid>
			</Grid>
		</MDBox>
	)
}

export default CallInsightsContent
